import {
  ContentNodeUtils,
  EnumContentNode,
  EnumValueContentNode,
  InputObjectContentNode,
  InputObjectDataTypeConfig,
  InputValueContentNode,
  InputValueDataTypeConfig,
  ListContentNode,
} from '@helo/cms'
import { pipe } from '@helo/utils'

/**
 * Local type for sort editor representation
 * contentNode is an InputObjectContentNode with a single inputField
 * The inputField's ofType should be OrderByEnum (EnumContentNode)
 */
export type SortItem = {
  propertyKey: string
  contentNode: OrderByInputObjectContentNode
}

export type SortedSortItem = SortItem & {
  sortOrder: number
  id: string
}

export type OrderByEnumContentNode = EnumContentNode<
  'OrderByEnum',
  EnumValueContentNode<'ASC' | 'DESC'>
>
export type OrderByInputObjectContentNode = InputObjectContentNode<
  string,
  InputValueContentNode<string, OrderByEnumContentNode>
>

export type OrderByInputObjectDataTypeConfig = InputObjectDataTypeConfig<
  string,
  InputValueDataTypeConfig<string, OrderByEnumContentNode>
>

/**
 * Extracts the property key from an InputObjectContentNode by finding the inputField with a value
 */
const getPropertyKeyFromContentNode = (
  contentNode: InputObjectContentNode,
): string | null => {
  // Find the inputField that has a value (only one should have a value for sort items)
  const inputFieldWithValue = contentNode.fields.find(field => {
    if (!('key' in field)) return false
    const inputValueField = field as InputValueContentNode
    // Check if the ofType is an ENUM and has a contentNode (selected enum value)
    if (
      inputValueField.ofType.kind === 'ENUM' &&
      (inputValueField.ofType as EnumContentNode).contentNode
    ) {
      return true
    }
    return false
  }) as InputValueContentNode | undefined

  if (!inputFieldWithValue || !('key' in inputFieldWithValue)) {
    return null
  }

  return inputFieldWithValue.key
}

/**
 * Converts a ListContentNode (orderBy) to SortItem array
 * Each item in the list is an INPUT_OBJECT with data like { fieldName: 'ASC' } or { fieldName: 'DESC' }
 */
export const contentNodeToSortItems = (
  orderByNode: ListContentNode | null,
): SortItem[] => {
  if (!orderByNode || !orderByNode.items || orderByNode.items.length === 0) {
    return []
  }

  return orderByNode.items
    .map(item => {
      if (item.kind !== 'INPUT_OBJECT') {
        return null
      }

      const propertyKey = getPropertyKeyFromContentNode(item)

      if (!propertyKey) {
        return null
      }

      return {
        propertyKey,
        contentNode: item,
      } as SortItem
    })
    .filter((item): item is SortItem => item !== null)
}

/**
 * Helper to get direction from contentNode by traversing to the enum value
 */
export const getDirectionFromContentNode = (
  contentNode: OrderByInputObjectContentNode,
  propertyKey: string,
): 'ASC' | 'DESC' => {
  return pipe(
    contentNode,
    ContentNodeUtils.findContentNode([
      ['key', propertyKey],
      ['kind', 'ENUM_VALUE'],
    ]),
    enumValueContentNode => {
      if (!enumValueContentNode)
        throw new Error(`Enum value not found for propertyKey: ${propertyKey}`)
      return enumValueContentNode as OrderByEnumContentNode
    },
    enumValueContentNode => enumValueContentNode.data ?? 'ASC',
  )
}

/**
 * Helper to create a new contentNode with propertyKey and direction
 */
export const createSortContentNode = (
  orderByInputObjectConfig: OrderByInputObjectDataTypeConfig,
  propertyKey: string,
  direction: 'ASC' | 'DESC',
): OrderByInputObjectContentNode => {
  // Create an empty InputObjectContentNode to start fresh
  const sortInputObject = ContentNodeUtils.createContentNodeWithSelection(
    orderByInputObjectConfig,
    {
      [propertyKey]: direction,
    },
  ) as InputObjectContentNode

  return sortInputObject as OrderByInputObjectContentNode
}
